import React, { useRef, useEffect } from "react"

import { useViewer } from "Pods/ThreeDStudio/Hooks/useViewer"
import { useViewerStore } from "Pods/ThreeDStudio/Hooks/useViewerStore"
import LoadingState from "Pods/ThreeDStudio/Shared/LoadingState/LoadingState"
import { ViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"
import { setViewerInstance } from "Pods/ThreeDStudio/Store/actions/staticViewer"
import {
  setViewerExplodedEnabled,
  setViewerIsRigged,
  setViewerMeshList,
  setViewerSegmentationRenderModeAvailable,
  setViewerSelectedMeshId,
} from "Pods/ThreeDStudio/Store/actions/viewer"

import { EMPTY_ARRAY } from "Utils/stableDefaults"

import ErrorState from "./Components/ErrorState/ErrorState"

function ViewerCanvas({ children }) {
  const canvasRef = useRef(null)

  const { viewer, isLoading: isViewerLoading, error } = useViewer(canvasRef, {
    dracoPath: "/draco/gltf/",
  })

  const models = useViewerStore(viewer, "models")
  const meshListState = useViewerStore(viewer, "meshList")
  const explodedViewState = useViewerStore(viewer, "explodedView")
  const renderModeState = useViewerStore(viewer, "renderMode")
  const selectionState = useViewerStore(viewer, "selection")
  const isModelLoading = models?.isLoading ?? true
  const loadedModelId = models?.loadedModels?.[0]?.id

  // The viewer sidebar (segment tab) renders outside ViewerContext, so mirror the
  // viewer instance + read-only state it needs into the store for it to consume.
  useEffect(() => {
    setViewerInstance(viewer ?? null)
  }, [viewer])

  useEffect(() => {
    setViewerMeshList(meshListState?.meshes ?? EMPTY_ARRAY)
  }, [meshListState])

  useEffect(() => {
    setViewerExplodedEnabled(Boolean(explodedViewState?.enabled))
  }, [explodedViewState])

  useEffect(() => {
    setViewerIsRigged(Boolean(viewer && loadedModelId && viewer.isModelRigged(loadedModelId)))
  }, [viewer, loadedModelId])

  useEffect(() => {
    setViewerSegmentationRenderModeAvailable(Boolean(renderModeState?.availableModes?.has("segmentation")))
  }, [renderModeState])

  useEffect(() => {
    setViewerSelectedMeshId(selectionState?.selectedMeshId ?? null)
  }, [selectionState])

  const hasModelUrl = useThreeDStudioStore(state => Boolean(state.viewer?.inputThreeD))
  // A swap holds the outgoing model's last frame on the canvas, so the loader
  // renders see-through over it instead of hiding it behind a blank panel.
  const isSwapping = useThreeDStudioStore(state => state.staticViewer?.isSwapping)

  return (
    <ViewerContext.Provider value={viewer}>
      <div className="viewer-root">
        {hasModelUrl && (isViewerLoading || isModelLoading)
          && <LoadingState isOverModel={Boolean(isSwapping)} />}
        {error && <ErrorState error={error} />}
        <canvas ref={canvasRef} className="viewer-canvas" />
        {viewer && children}
      </div>
    </ViewerContext.Provider>
  )
}

export default ViewerCanvas
